import type { JobKind, JobSummary } from "@contract";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../../i18n/errors";
import { ApiRequestError, errorCodeOf } from "../../api/fetcher";
import { useJobRetry } from "../../api/hooks";
import { Button } from "../../components/control/Button";
import { Banner } from "../../components/feedback/Banner";
import { ProgressBar } from "../../components/feedback/ProgressBar";
import { DateTime } from "../../components/format/DateTime";
import { RespendConfirmDialog } from "../../components/overlay/RespendConfirmDialog";
import { useBookContext } from "./bookContext";
import { failedJobKind } from "./steps";

/** The running job that reports the most detail, so the strip shows one bar rather than several. */
function leadJob(jobs: readonly JobSummary[]): JobSummary | undefined {
  const running = jobs.filter((job) => job.status === "running" || job.status === "queued");
  return running.find((job) => job.progress !== null) ?? running[0];
}

/**
 * What stopped, as a noun. The working strip's common:jobKind.* entries are
 * progressive ("Datei wird eingelesen"), which reads wrong in front of an
 * error sentence, so the three passes that can fail a Book (DESIGN "States")
 * have their own nouns. Static keys, not a lookup, so the catalogue check sees
 * each one referenced.
 */
function FailedJobLabel({ kind }: { kind: JobKind }) {
  const { t } = useTranslation("overview");
  switch (kind) {
    case "parse":
      return <>{t("failures.job.parse")}</>;
    case "chapterProposal":
      return <>{t("failures.job.chapterProposal")}</>;
    case "styleSheet":
      return <>{t("failures.job.styleSheet")}</>;
    default:
      return null;
  }
}

/**
 * The strip under the nav. It says one of two things: the Book is Failed, this
 * is the pass that stopped it (Parse, the Chapter Proposal, or the Style
 * Sheet: DESIGN "States"), and here is the retry for it, or a job is running
 * and this is what it is doing. A Book whose Chapters have not been proposed
 * yet has nothing in the table to look at, so without this the page would look
 * idle while Parse runs.
 */
export function BookStatusBanner() {
  const { t } = useTranslation(["overview", "errors", "common"]);
  const { book } = useBookContext();
  const retry = useJobRetry();

  if (book.state === "failed" && book.failure) {
    const jobId = book.retryJobId;
    const kind = failedJobKind(book);
    // A retry the service refused (409 JOB_NOT_RETRYABLE once the job is
    // running again, a 500, a dead connection) is said out loud: without it
    // the button just re-enables itself and the Author clicks again.
    const failedRetry = retry.isError
      ? errorMessage(
          t,
          errorCodeOf(retry.error),
          retry.error instanceof ApiRequestError ? retry.error.params : undefined,
        )
      : null;
    return (
      <Banner
        tone="danger"
        label={t("overview:failures.label")}
        action={
          jobId ? (
            <Button
              variant="secondary"
              size="sm"
              busy={retry.isPending}
              onClick={() => retry.mutate(jobId)}
            >
              {retry.isPending ? t("overview:failures.retrying") : t("overview:failures.retry")}
            </Button>
          ) : null
        }
      >
        <p>
          {kind ? (
            <span className="font-medium">
              <FailedJobLabel kind={kind} />
              {": "}
            </span>
          ) : null}
          {t(`errors:${book.failure.key}`)}
        </p>
        {failedRetry ? <p className="mt-1 font-medium text-danger">{failedRetry}</p> : null}
        <RespendConfirmDialog state={retry.respend} />
      </Banner>
    );
  }

  // The working strip belongs to the Book page. Every other screen has its own
  // account of the job it is about (the Style Sheet's progress, the Progress
  // screen's per-Chapter rows), and a second bar above them says the same
  // thing twice. A Failed Book, handled above, still shows everywhere: the
  // Author has to be able to retry from wherever they are.

  const job = leadJob(book.activeJobs);
  if (!job || job.manualRetryRequired) return null;
  const fraction =
    job.progress && job.progress.total > 0 ? job.progress.done / job.progress.total : null;
  // While Refinement works through the Chapters, the strip carries the count
  // that moves: how many are running, how many are waiting for their turn, and
  // how many already sit in Review. Without it, minutes of "in Arbeit" look
  // like nothing happening while the queue drains one Chapter at a time.
  const refineTally = (() => {
    if (job.kind !== "refine") return null;
    const running = book.activeJobs.filter(
      (j) => j.kind === "refine" && j.status === "running",
    ).length;
    const queued = book.activeJobs.filter(
      (j) => j.kind === "refine" && j.status === "queued",
    ).length;
    return running === 0 && queued === 0
      ? null
      : t("overview:refineTally", {
          running,
          queued,
          review: book.counts.inReview,
        });
  })();
  return (
    <Banner
      tone="working"
      action={
        fraction === null ? null : <ProgressBar value={fraction} showPercent className="w-40" />
      }
    >
      <span className="flex flex-col gap-0.5">
        <span>{t(`common:jobKind.${job.kind}`)}</span>
        {job.updatedAt ? (
          <span className="text-data text-ink-2">
            {t("overview:lastUpdated")} <DateTime value={job.updatedAt} style="dateTime" />
          </span>
        ) : null}
        {refineTally ? <span className="text-data text-ink-2">{refineTally}</span> : null}
      </span>
    </Banner>
  );
}
